import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { PaymentApi } from "../../api/PaymentApi";
import type { BalanceTransactionFetchOptions } from "../../generated/dto/BalanceTransactionFetchOptions";
import type { BalanceTransactionStaff } from "../../generated/dto/BalanceTransactionStaff";
import type { CashTransactionStaff } from "../../generated/dto/CashTransactionStaff";
import type { CsvExportOptions } from "../../generated/dto/CsvExportOptions";
import type { ManualCaptureResult } from "../../generated/dto/ManualCaptureResult";
import type { OrderFetchOptions } from "../../generated/dto/OrderFetchOptions";
import type { OrderPaymentFetchOptions } from "../../generated/dto/OrderPaymentFetchOptions";
import type { OrderPaymentRef } from "../../generated/dto/OrderPaymentRef";
import type { OrderPaymentStaff } from "../../generated/dto/OrderPaymentStaff";
import type { OrderPaymentStaffSearchParams } from "../../generated/dto/OrderPaymentStaffSearchParams";
import type { OrderPaymentStaffSearchResult } from "../../generated/dto/OrderPaymentStaffSearchResult";
import type { OrderRef } from "../../generated/dto/OrderRef";
import type { OrderSettlementTransferStaff } from "../../generated/dto/OrderSettlementTransferStaff";
import type { OrderStaff } from "../../generated/dto/OrderStaff";
import type { PaymentAttempt } from "../../generated/dto/PaymentAttempt";
import type { PaymentCustomerSession } from "../../generated/dto/PaymentCustomerSession";
import type { PaymentRefundFetchOptions } from "../../generated/dto/PaymentRefundFetchOptions";
import type { PaymentRefundStaff } from "../../generated/dto/PaymentRefundStaff";
import type { PaymentSendPaymentToReaderRequest } from "../../generated/dto/PaymentSendPaymentToReaderRequest";
import type { PaymentSourceStaff } from "../../generated/dto/PaymentSourceStaff";
import type { PaymentStartPaymentRequest } from "../../generated/dto/PaymentStartPaymentRequest";
import type { SettlementNormalizationPreview } from "../../generated/dto/SettlementNormalizationPreview";
import type { SettlementNormalizationResult } from "../../generated/dto/SettlementNormalizationResult";
import type { StripeTransactionStaff } from "../../generated/dto/StripeTransactionStaff";
import type { TransactionOtherStaff } from "../../generated/dto/TransactionOtherStaff";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class PaymentApiClient implements PaymentApi {
  constructor(private readonly client = useClient()) {}

  async getOrderPaymentStaff(id: number, options?: OrderPaymentFetchOptions): Promise<OrderPaymentStaff> {
    const { data } = await this.client.get<unknown>(`/orderpayment/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.OrderPaymentFetchOptions") });
    return deserializeCbiDto<OrderPaymentStaff>(data, "generated.dto.OrderPaymentStaff");
  }

  async getPaymentSourceStaff(id: number): Promise<PaymentSourceStaff> {
    const { data } = await this.client.get<unknown>(`/paymentsource/staff/${id}`);
    return deserializeCbiDto<PaymentSourceStaff>(data, "generated.dto.PaymentSourceStaff");
  }

  async getOrderSettlementTransferStaff(id: number): Promise<OrderSettlementTransferStaff> {
    const { data } = await this.client.get<unknown>(`/ordersettlementtransfer/staff/${id}`);
    return deserializeCbiDto<OrderSettlementTransferStaff>(data, "generated.dto.OrderSettlementTransferStaff");
  }

  async getStripeTransactionStaff(id: number): Promise<StripeTransactionStaff> {
    const { data } = await this.client.get<unknown>(`/stripetransaction/staff/${id}`);
    return deserializeCbiDto<StripeTransactionStaff>(data, "generated.dto.StripeTransactionStaff");
  }

  async getCashTransactionStaff(id: number): Promise<CashTransactionStaff> {
    const { data } = await this.client.get<unknown>(`/cashtransaction/staff/${id}`);
    return deserializeCbiDto<CashTransactionStaff>(data, "generated.dto.CashTransactionStaff");
  }

  async getTransactionOtherStaff(id: number): Promise<TransactionOtherStaff> {
    const { data } = await this.client.get<unknown>(`/transactionother/staff/${id}`);
    return deserializeCbiDto<TransactionOtherStaff>(data, "generated.dto.TransactionOtherStaff");
  }

  async searchOrderPaymentStaff(params: OrderPaymentStaffSearchParams, page: number, perPage: number): Promise<PaginationResult<OrderPaymentStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/orderpayment/staff/search`, serializeCbiDto(params, "generated.dto.OrderPaymentStaffSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<OrderPaymentStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<OrderPaymentStaffSearchResult>>(result.items, "generated.dto.OrderPaymentStaffSearchResult") };
    return result;
  }

  async exportOrderPaymentStaffSearchToCsv(params: OrderPaymentStaffSearchParams, options?: CsvExportOptions): Promise<string> {
    const { data } = await this.client.get<unknown>(`/orderpayment/staff/search/csv`, { params: { params: encodeRequestParam(params, "generated.dto.OrderPaymentStaffSearchParams"), options: options === undefined ? undefined : encodeRequestParam(options, "generated.dto.CsvExportOptions") } });
    return data as string;
  }

  async searchOrderPaymentStaffFull(params: OrderPaymentStaffSearchParams, page: number, perPage: number, options?: OrderPaymentFetchOptions): Promise<PaginationResult<OrderPaymentStaff>> {
    const { data } = await this.client.post<unknown>(`/orderpayment/staff/search/full`, serializeCbiDto(params, "generated.dto.OrderPaymentStaffSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.OrderPaymentFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<OrderPaymentStaff>;
    result = { ...result, items: deserializeCbiDto<Array<OrderPaymentStaff>>(result.items, "generated.dto.OrderPaymentStaff") };
    return result;
  }

  async getOrderPaymentStaffOrderId(id: number, options?: OrderFetchOptions): Promise<OrderStaff> {
    const { data } = await this.client.get<unknown>(`/orderpayment/staff/${id}/orderid`, { params: serializeCbiDto(options, "generated.dto.OrderFetchOptions") });
    return deserializeCbiDto<OrderStaff>(data, "generated.dto.OrderStaff");
  }

  async getOrderPaymentStaffStripeTransaction(id: number): Promise<StripeTransactionStaff | null> {
    const { data } = await this.client.get<unknown>(`/orderpayment/staff/${id}/stripetransaction`);
    return deserializeCbiDto<StripeTransactionStaff | null>(data, "generated.dto.StripeTransactionStaff");
  }

  async getOrderPaymentStaffRefunds(id: number, options?: PaymentRefundFetchOptions): Promise<Array<PaymentRefundStaff>> {
    const { data } = await this.client.get<unknown>(`/orderpayment/staff/${id}/refunds`, { params: serializeCbiDto(options, "generated.dto.PaymentRefundFetchOptions") });
    return deserializeCbiDto<Array<PaymentRefundStaff>>(data, "generated.dto.PaymentRefundStaff");
  }

  async getOrderPaymentStaffCashTransaction(id: number): Promise<CashTransactionStaff | null> {
    const { data } = await this.client.get<unknown>(`/orderpayment/staff/${id}/cashtransaction`);
    return deserializeCbiDto<CashTransactionStaff | null>(data, "generated.dto.CashTransactionStaff");
  }

  async getOrderPaymentStaffBalanceTransaction(id: number, options?: BalanceTransactionFetchOptions): Promise<BalanceTransactionStaff | null> {
    const { data } = await this.client.get<unknown>(`/orderpayment/staff/${id}/balancetransaction`, { params: serializeCbiDto(options, "generated.dto.BalanceTransactionFetchOptions") });
    return deserializeCbiDto<BalanceTransactionStaff | null>(data, "generated.dto.BalanceTransactionStaff");
  }

  async getOrderPaymentStaffTransactionOther(id: number): Promise<TransactionOtherStaff | null> {
    const { data } = await this.client.get<unknown>(`/orderpayment/staff/${id}/transactionother`);
    return deserializeCbiDto<TransactionOtherStaff | null>(data, "generated.dto.TransactionOtherStaff");
  }

  async cancelPaymentStaff(payment: OrderPaymentRef): Promise<void> {
    await this.client.post(`/payment/staff/cancelpayment`, serializeCbiDto(payment, "generated.dto.OrderPaymentRef"));
  }

  async cancelPaymentMember(payment: OrderPaymentRef): Promise<void> {
    await this.client.post(`/payment/member/cancelpayment`, serializeCbiDto(payment, "generated.dto.OrderPaymentRef"));
  }

  async completePaymentStaff(order: OrderRef): Promise<void> {
    await this.client.post(`/payment/staff/completepayment`, serializeCbiDto(order, "generated.dto.OrderRef"));
  }

  async completePaymentMember(order: OrderRef): Promise<void> {
    await this.client.post(`/payment/member/completepayment`, serializeCbiDto(order, "generated.dto.OrderRef"));
  }

  async manualCapturePaymentsStaff(includePreviousDays: boolean): Promise<ManualCaptureResult> {
    const { data } = await this.client.post<unknown>(`/payment/staff/manualcapturepayments`, includePreviousDays);
    return deserializeCbiDto<ManualCaptureResult>(data, "generated.dto.ManualCaptureResult");
  }

  async paymentAttemptStaff(payment: OrderPaymentRef): Promise<PaymentAttempt> {
    const { data } = await this.client.post<unknown>(`/payment/staff/paymentattempt`, serializeCbiDto(payment, "generated.dto.OrderPaymentRef"));
    return deserializeCbiDto<PaymentAttempt>(data, "generated.dto.PaymentAttempt");
  }

  async paymentAttemptMember(payment: OrderPaymentRef): Promise<PaymentAttempt> {
    const { data } = await this.client.post<unknown>(`/payment/member/paymentattempt`, serializeCbiDto(payment, "generated.dto.OrderPaymentRef"));
    return deserializeCbiDto<PaymentAttempt>(data, "generated.dto.PaymentAttempt");
  }

  async paymentAttemptsStaff(order: OrderRef): Promise<Array<PaymentAttempt>> {
    const { data } = await this.client.post<unknown>(`/payment/staff/paymentattempts`, serializeCbiDto(order, "generated.dto.OrderRef"));
    return deserializeCbiDto<Array<PaymentAttempt>>(data, "generated.dto.PaymentAttempt");
  }

  async paymentAttemptsMember(order: OrderRef): Promise<Array<PaymentAttempt>> {
    const { data } = await this.client.post<unknown>(`/payment/member/paymentattempts`, serializeCbiDto(order, "generated.dto.OrderRef"));
    return deserializeCbiDto<Array<PaymentAttempt>>(data, "generated.dto.PaymentAttempt");
  }

  async paymentCustomerSessionStaff(payment: OrderPaymentRef): Promise<PaymentCustomerSession> {
    const { data } = await this.client.post<unknown>(`/payment/staff/paymentcustomersession`, serializeCbiDto(payment, "generated.dto.OrderPaymentRef"));
    return deserializeCbiDto<PaymentCustomerSession>(data, "generated.dto.PaymentCustomerSession");
  }

  async paymentCustomerSessionMember(payment: OrderPaymentRef): Promise<PaymentCustomerSession> {
    const { data } = await this.client.post<unknown>(`/payment/member/paymentcustomersession`, serializeCbiDto(payment, "generated.dto.OrderPaymentRef"));
    return deserializeCbiDto<PaymentCustomerSession>(data, "generated.dto.PaymentCustomerSession");
  }

  async preparePaymentStaff(payment: OrderPaymentRef): Promise<PaymentAttempt> {
    const { data } = await this.client.post<unknown>(`/payment/staff/preparepayment`, serializeCbiDto(payment, "generated.dto.OrderPaymentRef"));
    return deserializeCbiDto<PaymentAttempt>(data, "generated.dto.PaymentAttempt");
  }

  async preparePaymentMember(payment: OrderPaymentRef): Promise<PaymentAttempt> {
    const { data } = await this.client.post<unknown>(`/payment/member/preparepayment`, serializeCbiDto(payment, "generated.dto.OrderPaymentRef"));
    return deserializeCbiDto<PaymentAttempt>(data, "generated.dto.PaymentAttempt");
  }

  async previewMultiDaySettlementStaff(order: OrderRef): Promise<SettlementNormalizationPreview> {
    const { data } = await this.client.post<unknown>(`/payment/staff/previewmultidaysettlement`, serializeCbiDto(order, "generated.dto.OrderRef"));
    return deserializeCbiDto<SettlementNormalizationPreview>(data, "generated.dto.SettlementNormalizationPreview");
  }

  async repairMultiDaySettlementStaff(order: OrderRef): Promise<SettlementNormalizationResult> {
    const { data } = await this.client.post<unknown>(`/payment/staff/repairmultidaysettlement`, serializeCbiDto(order, "generated.dto.OrderRef"));
    return deserializeCbiDto<SettlementNormalizationResult>(data, "generated.dto.SettlementNormalizationResult");
  }

  async sendPaymentToReaderStaff(request: PaymentSendPaymentToReaderRequest): Promise<void> {
    await this.client.post(`/payment/staff/sendpaymenttoreader`, serializeCbiDto(request, "generated.dto.PaymentSendPaymentToReaderRequest"));
  }

  async startPaymentStaff(request: PaymentStartPaymentRequest): Promise<OrderPaymentRef> {
    const { data } = await this.client.post<unknown>(`/payment/staff/startpayment`, serializeCbiDto(request, "generated.dto.PaymentStartPaymentRequest"));
    return deserializeCbiDto<OrderPaymentRef>(data, "generated.dto.OrderPaymentRef");
  }

  async startPaymentMember(request: PaymentStartPaymentRequest): Promise<OrderPaymentRef> {
    const { data } = await this.client.post<unknown>(`/payment/member/startpayment`, serializeCbiDto(request, "generated.dto.PaymentStartPaymentRequest"));
    return deserializeCbiDto<OrderPaymentRef>(data, "generated.dto.OrderPaymentRef");
  }
}

export const usePaymentApi = (): PaymentApi => new PaymentApiClient();
